import { Injectable } from "@angular/core";
import { UUID } from "angular2-uuid";
import { StoredStateData } from "../models/stored-state-data";
import { StoredCachedService } from "../models/stored-cached-service";
import { DispatchedServiceViewModel } from "../../models/view-models/dispatched-service.view-model";

/**
 * Class for mapping a StateData to a StoredStateData.
 */
@Injectable({
	providedIn: 'root'
})
export class StateDataMapper {
	/**
	 * Maps a DispatchedServiceViewModel object to a StoredStateData object.
	 * @param service The dispatched service view model to convert.
	 * @param id The unique storage Id for the output StoredDispatchedServiceState.
	 * @param userName The username for the output StoredDispatchedServiceState.
	 * @param crownId The crown Id for the output StoredDispatchedServiceState.
	 * @param createdDate The date the storage model was created.
	 * @returns A StoredStateData object.
	 */
	public ToStored(service: DispatchedServiceViewModel, id: UUID, userName: string, crownId: string, createdDate: Date | null): StoredStateData {
		return {
			LegacyId: service.LegacyId,
			Id: id,
			UserName: userName,
			CreatedDate: createdDate ?? new Date(),
			CrownId: crownId,
			RetrievedDate: service.RetrievedDate,
			AcceptedDate: service.AcceptedDate,
			ArrivedDate: service.ArrivedDate,
			CompletedDate: service.CompletedDate,
			CannotCompleteDate: service.CannotCompleteDate,
			PicturesCompleteDate: service.PicturesCompleteDate,
			OsdCompleteDate: service.OsdCompleteDate,
			SignatureCompleteDate: service.SignatureCompleteDate,
			NotesCompleteDate: service.NotesCompleteDate,
			SignerCompleteDate: service.SignerCompleteDate,
			RefusedDate: service.RefusedDate,
			DocumentsDownloadedDate: service.DocumentsDownloadedDate,
			DocumentsCompleteDate: service.DocumentsCompleteDate,
			PreviousCannotCompleteDate: service.PreviousCannotCompleteDate,
			IsEndOfRouteComplete: service.IsEndOfRouteComplete,
			IsVisibleToDriver: service.IsVisibleToDriver,
			IsCancelled: service.IsCancelled,
			Sequence: service.Sequence
		};
	}

	/**
	 * Builds a defaulted state row from an orphaned cache row. All workflow dates are null and the
	 * service is marked visible so it immediately appears in the driver's route list.
	 * @param cached The orphaned cache row to derive identity and dates from.
	 * @param sequence The sequence value to assign so the service sorts to the end of the list.
	 * @param userName The username of the current user.
	 * @param crownId The Crown identifier of the current user.
	 * @returns A fully populated StoredStateData ready for insertion.
	 */
	public ToDefaultStateData(cached: StoredCachedService, sequence: number, userName: string, crownId: string): StoredStateData {
		return {
			Id: cached.Id,
			LegacyId: cached.LegacyId,
			UserName: userName,
			CrownId: crownId,
			CreatedDate: cached.CreatedDate,
			RetrievedDate: cached.CreatedDate,
			AcceptedDate: null,
			ArrivedDate: null,
			CompletedDate: null,
			CannotCompleteDate: null,
			PicturesCompleteDate: null,
			OsdCompleteDate: null,
			SignatureCompleteDate: null,
			SignerCompleteDate: null,
			NotesCompleteDate: null,
			RefusedDate: null,
			DocumentsDownloadedDate: null,
			DocumentsCompleteDate: null,
			PreviousCannotCompleteDate: null,
			IsEndOfRouteComplete: false,
			IsVisibleToDriver: true,
			IsCancelled: false,
			Sequence: sequence
		};
	}

	/**
	 * Maps a StoredStateData object to a DispatchedServiceViewModel object.
	 * @param storedServiceState The dispatched service state data to convert.
	 * @param viewModel The dispatched service view model to convert.
	 * @returns A StateData object.
	 */
	public FromStored(storedServiceState: StoredStateData, viewModel: DispatchedServiceViewModel): DispatchedServiceViewModel {
		viewModel.RetrievedDate = storedServiceState.RetrievedDate;
		viewModel.AcceptedDate = storedServiceState.AcceptedDate;
		viewModel.ArrivedDate = storedServiceState.ArrivedDate;
		viewModel.CompletedDate = storedServiceState.CompletedDate;
		viewModel.CannotCompleteDate = storedServiceState.CannotCompleteDate;
		viewModel.PicturesCompleteDate = storedServiceState.PicturesCompleteDate;
		viewModel.OsdCompleteDate = storedServiceState.OsdCompleteDate;
		viewModel.SignatureCompleteDate = storedServiceState.SignatureCompleteDate;
		viewModel.SignerCompleteDate = storedServiceState.SignerCompleteDate;
		viewModel.NotesCompleteDate = storedServiceState.NotesCompleteDate;
		viewModel.RefusedDate = storedServiceState.RefusedDate;
		viewModel.DocumentsDownloadedDate = storedServiceState.DocumentsDownloadedDate;
		viewModel.DocumentsCompleteDate = storedServiceState.DocumentsCompleteDate;
		viewModel.PreviousCannotCompleteDate = storedServiceState.PreviousCannotCompleteDate;
		viewModel.IsEndOfRouteComplete = storedServiceState.IsEndOfRouteComplete;
		viewModel.IsVisibleToDriver = storedServiceState.IsVisibleToDriver;
		viewModel.IsCancelled = storedServiceState.IsCancelled ?? false;
		viewModel.Sequence = storedServiceState.Sequence;

		return viewModel;
	}
}